import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Drawer, Select, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Button } from '@surecompanies/core_components'

import { VcnManagementCompanySelect } from './VcnManagementCompanySelect'
import type {
  VcnCardInUseStatus,
  VcnCardStatus,
  VcnManagementCardFilters,
  VcnProviderFilter,
} from './types'
import {
  buildCardFiltersFromSidebarDraft,
  CARD_STATUS_OPTIONS,
  DEFAULT_VCN_MANAGEMENT_CARD_FILTERS,
  FILTER_ALL_VALUE,
  fromFilterSelectValue,
  IN_USE_OPTIONS,
  PROVIDER_OPTIONS,
  toFilterSelectValue,
} from './vcnManagementFilterDefaults'
import { useVcnManagementCarriers } from './hooks/useVcnManagementCarriers'
import {
  FilterSection,
  FilterSectionTitle,
  FilterLabel,
  Footer,
  ResetButton,
} from './VcnManagementSidebar.styles'

const EMPTY_COMPANY_KEYS: string[] = []
const EMPTY_IN_USE: VcnCardInUseStatus[] = []

const CARRIER_ORPHAN_NOTE =
  'Filtering by carrier necessarily excludes orphan cards: a carrier belongs to the plan, and a card carries no plan reference.'

export interface VcnManagementCardSidebarProps {
  open: boolean
  onClose: () => void
  appliedFilters: VcnManagementCardFilters
  onApply: (next: VcnManagementCardFilters) => void
}

function draftFromApplied(
  applied: VcnManagementCardFilters
): VcnManagementCardFilters {
  return {
    clientPublicKeys: applied.clientPublicKeys,
    carrierName: applied.carrierName,
    vcnProvider: applied.vcnProvider,
    cardStatus: applied.cardStatus,
    inUse: applied.inUse,
  }
}

/**
 * Card Mode's filters. Deliberately not Benefit Mode's drawer with a flag:
 * most of that set has no card-grain meaning.
 *
 * - `Primary payment method` is dropped — every row here is a card, so it is
 *   always VCN and filters nothing.
 * - `Include inactive benefits` is dropped — subsumed by `Plan`.
 * - `Card status` is the CARD's own status, where Benefit Mode's same-looking
 *   filter is the benefit's.
 * - `Client` comes from the card HOLDER, not a bound benefit, so an orphan card
 *   stays findable by employer — which is the whole point of the tab.
 *
 * `Plan` is a sidebar filter rather than only a tile shortcut because normal
 * filters are disabled while a tile filter is active: with tiles alone,
 * "show me Acme's orphan cards" would be impossible.
 */
export function VcnManagementCardSidebar({
  open,
  onClose,
  appliedFilters,
  onApply,
}: VcnManagementCardSidebarProps) {
  const [draft, setDraft] = useState<VcnManagementCardFilters>(
    DEFAULT_VCN_MANAGEMENT_CARD_FILTERS
  )

  const { data: carrierNames, isLoading: isLoadingCarriers } =
    useVcnManagementCarriers()

  const carrierOptions = useMemo(() => {
    const dynamic = (carrierNames ?? []).map((name) => ({
      value: name,
      label: name,
    }))
    return [{ value: FILTER_ALL_VALUE, label: 'All carriers' }, ...dynamic]
  }, [carrierNames])

  useEffect(() => {
    if (!open) return
    setDraft(draftFromApplied(appliedFilters))
  }, [open, appliedFilters])

  const handleApply = useCallback(() => {
    onApply(buildCardFiltersFromSidebarDraft(appliedFilters, draft))
    onClose()
  }, [appliedFilters, draft, onApply, onClose])

  const handleReset = useCallback(() => {
    setDraft({ ...DEFAULT_VCN_MANAGEMENT_CARD_FILTERS })
  }, [])

  return (
    <Drawer
      title={<span style={{ fontWeight: 600, fontSize: 16 }}>Filters</span>}
      placement="right"
      onClose={onClose}
      open={open}
      width={420}
      zIndex={1050}
      destroyOnClose={false}
      maskStyle={{ backgroundColor: 'rgba(0, 49, 70, 0.42)' }}
      drawerStyle={{ boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.16)' }}
      headerStyle={{ padding: '16px 24px', borderBottom: '1px solid #e8e8e8' }}
      bodyStyle={{ padding: '16px 24px 24px' }}
      footerStyle={{
        borderTop: '1px solid #e8e8e8',
        padding: '16px 24px',
        background: '#fafafa',
      }}
      footer={
        <Footer>
          <Button type="primary" onClick={handleApply}>
            Apply filters
          </Button>
          <ResetButton onClick={handleReset}>Reset to defaults</ResetButton>
        </Footer>
      }
    >
      <FilterSection>
        <FilterSectionTitle>Plan</FilterSectionTitle>
        <FilterLabel>Whether a benefit is using this card</FilterLabel>
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%' }}
          size="large"
          placeholder="Any"
          options={[...IN_USE_OPTIONS]}
          value={draft.inUse ?? EMPTY_IN_USE}
          onChange={(values: VcnCardInUseStatus[]) =>
            setDraft((d) => ({
              ...d,
              inUse: values.length > 0 ? values : undefined,
            }))
          }
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>Client</FilterSectionTitle>
        <FilterLabel>Employer of the card holder</FilterLabel>
        <VcnManagementCompanySelect
          immediateCommit
          style={{ width: '100%' }}
          value={draft.clientPublicKeys ?? EMPTY_COMPANY_KEYS}
          onCommit={(keys) =>
            setDraft((d) => ({
              ...d,
              clientPublicKeys: keys.length > 0 ? keys : undefined,
            }))
          }
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>
          Carrier{' '}
          <Tooltip title={CARRIER_ORPHAN_NOTE}>
            <InfoCircleOutlined
              style={{ color: '#8c8c8c', fontSize: 12 }}
              aria-label={CARRIER_ORPHAN_NOTE}
            />
          </Tooltip>
        </FilterSectionTitle>
        <FilterLabel>Carrier name</FilterLabel>
        <Select
          // Type-to-filter, matching Application Engine's carrier field: the
          // list is every carrier with a benefit this plan year, which is long
          // enough that scrolling it is the wrong interaction.
          showSearch
          style={{ width: '100%' }}
          size="large"
          loading={isLoadingCarriers}
          options={carrierOptions}
          value={toFilterSelectValue(draft.carrierName)}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={(value) =>
            setDraft((d) => ({
              ...d,
              carrierName: fromFilterSelectValue<string>(value),
            }))
          }
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>VCN Provider</FilterSectionTitle>
        <FilterLabel>Provider</FilterLabel>
        <Select
          style={{ width: '100%' }}
          size="large"
          options={[...PROVIDER_OPTIONS]}
          value={toFilterSelectValue(draft.vcnProvider)}
          onChange={(value) =>
            setDraft((d) => ({
              ...d,
              vcnProvider: fromFilterSelectValue<VcnProviderFilter>(value),
            }))
          }
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>Card status</FilterSectionTitle>
        <FilterLabel>Status of the card itself</FilterLabel>
        <Select
          style={{ width: '100%' }}
          size="large"
          options={[...CARD_STATUS_OPTIONS]}
          value={toFilterSelectValue(draft.cardStatus)}
          onChange={(value) =>
            setDraft((d) => ({
              ...d,
              cardStatus: fromFilterSelectValue<VcnCardStatus>(value),
            }))
          }
        />
      </FilterSection>
    </Drawer>
  )
}
